import { useState, useRef, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { ManualIcon } from "../../../assets/icons";
import "./FloatingManualButton.scss";

const STORAGE_KEY = "archeo-floating-manual-top-v2";
const EDGE_MARGIN = 12;
const DEFAULT_HEIGHT = 68;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const getBounds = (elementHeight) => ({
  min: EDGE_MARGIN,
  max: Math.max(
    EDGE_MARGIN,
    window.innerHeight - elementHeight - EDGE_MARGIN
  ),
});

const getDefaultTop = () => window.innerHeight * 0.9 - DEFAULT_HEIGHT;

const getInitialTop = () => {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved === null) return getDefaultTop();

  const parsed = Number(saved);
  if (Number.isNaN(parsed)) return getDefaultTop();

  const { min, max } = getBounds(DEFAULT_HEIGHT);
  return clamp(parsed, min, max);
};

const DragGripIcon = () => (
  <svg
    className="floating-menu__grip-icon"
    width="10"
    height="24"
    viewBox="0 0 10 24"
    aria-hidden="true"
  >
    {[6, 12, 18].map((cy) =>
      [3, 7].map((cx) => (
        <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="1.4" fill="currentColor" />
      ))
    )}
  </svg>
);

const FloatingManualButton = ({
  currentPath,
  isActive,
  onNavClick,
}) => {
  const containerRef = useRef(null);
  const dragRef = useRef({ active: false, startY: 0, startTop: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [top, setTop] = useState(getInitialTop);

  const applyClampedTop = useCallback((value) => {
    const height = containerRef.current?.offsetHeight || DEFAULT_HEIGHT;
    const { min, max } = getBounds(height);
    return clamp(value, min, max);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setTop((prev) => applyClampedTop(prev));
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [applyClampedTop]);

  useEffect(() => {
    setTop((prev) => applyClampedTop(prev));
  }, [applyClampedTop]);

  const handleDragStart = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragRef.current = {
      active: true,
      startY: event.clientY,
      startTop: top,
    };
    setIsDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleDragMove = (event) => {
    if (!dragRef.current.active) return;

    const deltaY = event.clientY - dragRef.current.startY;
    setTop(applyClampedTop(dragRef.current.startTop + deltaY));
  };

  const handleDragEnd = (event) => {
    if (!dragRef.current.active) return;

    const deltaY = event.clientY - dragRef.current.startY;
    const nextTop = applyClampedTop(dragRef.current.startTop + deltaY);
    setTop(nextTop);
    localStorage.setItem(STORAGE_KEY, String(nextTop));

    dragRef.current.active = false;
    setIsDragging(false);
    event.currentTarget.releasePointerCapture(event.pointerId);
  };

  if (currentPath === "/manualeutente") return null;

  return (
    <div
      ref={containerRef}
      className={`floating-menu${isDragging ? " floating-menu--dragging" : ""}`}
      style={{ top: `${top}px` }}
    >
      <Link
        onClick={onNavClick}
        name="manuale"
        to="/manualeutente"
        className={`nav-link floating-menu__link${isActive ? " active" : ""}`}
        draggable={false}
      >
        <ManualIcon height="40px" width="40px" />
      </Link>
      <button
        type="button"
        className="floating-menu__drag-handle"
        aria-label="Trascina su e giù"
        onPointerDown={handleDragStart}
        onPointerMove={handleDragMove}
        onPointerUp={handleDragEnd}
        onPointerCancel={handleDragEnd}
      >
        <DragGripIcon />
      </button>
    </div>
  );
};

export default FloatingManualButton;
